import myronLogo from "@/assets/MYRON-icon-transparent.webp";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import { JSX, useEffect, useState } from "react";
import {
  Bars3Icon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useCurrentOrganization, useCurrentEntityDetail, useMerchantActivePool } from "@/hooks/queries";
import { useBlockchainData } from "@/context/BlockchainContext";
import Navlinks from "./Navlinks";
import Wallet from "./Wallet";
import Profile from "./Profile";
import Transaction from "./Transaction";
import { useCurrentUser } from "@/hooks";
import { UserType, UserRole } from "@/types";
import { OrganizationType } from "@/types/domain";
import { useWalletSetupOptional } from "@/context/WalletSetupProvider";
import { hasEntityPublicKey } from "@/utils/helpers";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/opacity.css";
import { useTheme } from "@/context/ThemeProvider";
import { cn } from "@/lib/utils";

type Props = {
  className?: string;
}

export const EURM_ADDRESS = import.meta.env.VITE_EURO_MYRON_ADDRESS;

/**
 * Header component for the application.
 *
 * Displays the logo, navigation links, user profile, and wallet balance.
 * Redirects merchants to the payment page when an interest or end payment is due
 */
const Header = ({className }: Props): JSX.Element => {

  // Hooks
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { darkMode } = useTheme();

  // Query hooks
  const { data: user, isPending: isUserPending } = useCurrentUser();
  const blockchainData = useBlockchainData();
  const { data: organization } = useCurrentOrganization();
  const { data: entity } = useCurrentEntityDetail();
  const isMerchant = organization?.externalEntityType === OrganizationType.Merchant;
  const { data: poolData } = useMerchantActivePool(isMerchant ? (organization?.externalEntityId ?? undefined) : undefined);

  const [sideMenuOpen, setSideMenuOpen] = useState(false);

  const blockchainReady =
    blockchainData.initialized &&
    !blockchainData.loading &&
    blockchainData.chainStateReady;
  const hasWallet = hasEntityPublicKey(entity?.publicKey);
  const walletSetup = useWalletSetupOptional();
  const lockNavigation = walletSetup?.locked ?? false;
  const paymentDue =
    blockchainData.merchant.interestPaymentDue ||
    blockchainData.inventoryProvider.isEndPaymentDue === true;

  // Force merchants onto Make Payment when interest/end payment is due.
  // Skip when already there, or when they still need to set up a wallet.
  useEffect(() => {
    if (!user || user.userType === UserType.Developer) return;
    if (!hasWallet || lockNavigation) return;
    if (!blockchainReady || !paymentDue) return;
    if (
      pathname === "/user/makepayment" ||
      pathname === "/login" ||
      pathname.includes("/transactions")
    ) {
      return;
    }
    void navigate({ to: "/user/makepayment" });
  }, [blockchainReady, hasWallet, lockNavigation, navigate, pathname, paymentDue, user]);

  if (isUserPending || !user) {
    return <></>;
  }

  return (
    <div
      className={cn(
        "overflow-visible select-none w-full dark:bg-mfdarklight dark:shadow-gray-950 items-center top-0 h-auto bg-white shadow-sm z-100 px-4 md:px-5",
        className
      )}
    >
      <div className="flex w-full justify-between items-center mt-1 mb-1 py-1">
        {/* Logo */}
        <Link className="" to={lockNavigation ? "/user/wallet-setup" : user.userType === UserType.Developer ? "/admin/dashboard" : "/user/dashboard"}>
          <LazyLoadImage
            alt={"Myron logo"}
            className="flex min-w-[50px] max-w-[65px] lg:max-w-[75px] ml-2 md:ml-3"
            src={myronLogo}
            effect="opacity"
          />
        </Link>

        <div className="flex items-center space-x-1">
          <Wallet blockchainData={blockchainData} darkMode={darkMode} />
        </div>
      </div>
      {/* Bottom Container --> Logo; NavLinks; Profile; Dark Mode */}
      <div className="flex items-center justify-between pb-1">
        {/* Navbar/Side Menu Links */}
        <Navlinks
          user={user}
          assetType={poolData?.assetType}
          entityType={organization?.externalEntityType}
          sideMenuOpen={sideMenuOpen}
          setSideMenuOpen={setSideMenuOpen}
          disabled={lockNavigation}
        />

        {/* Right Nav Container */}
        <div className="flex gap-2 w-full items-center justify-end">
          {/* EURM Shortcut */}
          {
            user.userRole === UserRole.Superuser && hasWallet &&
            <Transaction disabled={lockNavigation} />
          }

          {/* Profile & Dropdown Menu */}
          <Profile user={user} entity={entity} />

          {/* Side Menu Icon */}
          <div
            role="button"
            aria-label="Toggle menu"
            onClick={() => setSideMenuOpen((prev) => !prev)}
            className="md:-ml-2 select-none cursor-pointer text-gray-800 transition-all ease-in-out duration-300 active:rotate-180"
          >
            {
              sideMenuOpen ?
                <XMarkIcon className="md:hidden w-8 h-8 dark:text-mforange" />:
                <Bars3Icon className="md:hidden w-8 h-8 dark:text-mforange" />
            }
          </div>
        </div>
      </div>
    </div>
  );
};
Header.displayName = "Header";

export default Header;
